// ** Modules
import { AnimatePresence, motion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ButtonBase, useTheme } from '@mui/material';

// ** Components
import { Icon } from '@/components/common/icons';

// ** Types
import type { AccordionWrapperProps } from './index';

const AccordionWrapper = ({
	id,
	title,
	isExpanded,
	isActive,
	paused,
	toggle,
	autoAdvanceDuration,
	handleCountdownComplete,
	children
}: AccordionWrapperProps) => {
	const theme = useTheme();

	return (
		<Box sx={{ width: '100%' }}>
			<ButtonBase
				onClick={() => toggle()}
				aria-expanded={isExpanded}
				aria-controls={`dropdown-accordion-panel-${id}`}
				disableRipple
				sx={{
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pl: 0,
					pr: 3,
					pt: 6,
					pb: 4,
					textAlign: 'left',
					'&:hover, &:active, &:focusVisible': {
						backgroundColor: 'transparent',
						outline: 'none'
					},
					'&:hover .chevron-wrap': {
						opacity: 0.5,
						transform: 'scale(1.25)'
					}
				}}
			>
				<Typography variant="title-sm" sx={{ color: theme.palette.text.primary }}>
					{title}
				</Typography>

				<Box
					className="chevron-wrap"
					sx={{
						display: 'flex',
						alignItems: 'center',
						transition: 'opacity 220ms ease, transform 220ms ease'
					}}
				>
					<Icon
						name="chevron"
						expanded={isExpanded}
						width={16}
						height={8}
						color={theme.palette.text.secondary ?? theme.palette.text.primary}
					/>
				</Box>
			</ButtonBase>

			<AnimatePresence initial={false}>
				{isExpanded && (
					<motion.div
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
						style={{ overflow: 'hidden' }}
					>
						<Box sx={{ pb: 6 }}>{children}</Box>
					</motion.div>
				)}
			</AnimatePresence>

			{isActive && !paused ? (
				<motion.div
					key={`progress-${id}`}
					initial={{ scaleX: 1 }}
					animate={{ scaleX: 0 }}
					transition={{ duration: autoAdvanceDuration, ease: 'linear' }}
					onAnimationComplete={handleCountdownComplete}
					style={{
						width: '100%',
						height: 1,
						backgroundColor: theme.palette.primary.main,
						transformOrigin: 'right'
					}}
				/>
			) : (
				<Box
					sx={{
						width: '100%',
						borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
					}}
				/>
			)}
		</Box>
	);
};

export default AccordionWrapper;
